import { Ref, useEffect, useMemo } from "react"
import { getIn } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import type { FormikProps } from "formik"
import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { FormikSwitch, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { LabeledField } from "../../../../../../shared/components/forms/LabeledField"
import { StandardFormikErrorScrollWrapper } from "../../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { FormikImageUpload } from "../../../../../../shared/components/Formik/FormikImageUpload"
import { FormikAllergensField } from "../../../../admin/products/components/FormikAllergensField"
import { FormikCurrencyField } from "../../../../admin/products/components/FormikCurrencyField"

import { buildDishValidationSchema } from "../helpers"
import { DishFormValues, DishParameterKey } from "../menuTemplatesTypes"
import { DishAvailabilityField } from "./DishAvailabilityField"
import { DishFieldCard } from "./DishFieldCard"

const useStyles = makeStyles()((theme) => ({
    nameSection: {
        padding: "1.5rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".625rem",
        background: theme.palette.background.paper,
    },
    sectionSubtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    priceField: {
        maxWidth: "11rem",
    },
    currency: {
        color: theme.palette.tailwind.slate[500],
    },
    sameWeekToggle: {
        "& .MuiFormControl-root": {
            width: "auto",
        },
    },
}))

/** Mirrors the part of Formik's state a page-header action button outside the Formik needs, to follow the disable-after-invalid-submit rule. */
export type FormSubmitState = Readonly<{ isValid: boolean; submitCount: number }>

type Props = Readonly<{
    /** Lets the parent read the form state, e.g. `dirty` for the discard-changes guard. */
    innerRef: Ref<FormikProps<DishFormValues>>
    initialValues: DishFormValues
    onDone: (dish: DishFormValues) => void
    /** The Done button lives in the page header, outside this Formik, so its disabled state is reported up here. */
    onStateChange: (state: FormSubmitState) => void
}>

// The drill-in is its own form: its submit count, touched state and errors must not bleed into
// (or from) the surrounding template form. Only submitting it — from the page header — commits the dish.
export function DishEditor({ innerRef, initialValues, onDone, onStateChange }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const requiredMessage = l10n.getString("merchant-menu-templates-validation-required")
    const numberMessage = l10n.getString("merchant-menu-templates-validation-number")
    const validationSchema = useMemo(
        () => buildDishValidationSchema(requiredMessage, numberMessage),
        [requiredMessage, numberMessage],
    )

    return (
        <Formik<DishFormValues>
            innerRef={innerRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onDone}
        >
            {({ values, errors, touched, submitCount, isValid }) => {
                const fieldHasError = (field: string) =>
                    Boolean(getIn(errors, field)) && (submitCount > 0 || Boolean(getIn(touched, field)))

                useEffect(() => {
                    onStateChange({ isValid, submitCount })
                }, [isValid, submitCount, onStateChange])

                return (
                    <StandardFormikErrorScrollWrapper<DishFormValues>>
                        <Stack spacing="1rem">
                            <Stack spacing="1.5rem" className={classes.nameSection}>
                                <LabeledField
                                    label={
                                        <LocalizedStrict id="merchant-menu-templates-dish-name-label">
                                            <span>Dish Name</span>
                                        </LocalizedStrict>
                                    }
                                >
                                    <FormikTextField name="name" fullWidth />
                                </LabeledField>
                                <DishAvailabilityField />
                            </Stack>

                            <Stack spacing=".5rem">
                                <LocalizedStrict id="merchant-menu-templates-dish-fields-title">
                                    <Typography variant="h6">Fields</Typography>
                                </LocalizedStrict>

                                <Stack spacing="1rem">
                                    <DishFieldCard
                                        fieldKey={DishParameterKey.TITLE}
                                        namePrefix="title"
                                        config={values.title}
                                        hasError={fieldHasError("title")}
                                        aiFill={values.title.aiFill}
                                    >
                                        <LocalizedStrict
                                            id="merchant-menu-templates-title-value-field"
                                            attrs={{ placeholder: true }}
                                        >
                                            <FormikTextField name="title.value" placeholder="Title text…" fullWidth />
                                        </LocalizedStrict>
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.DESCRIPTION}
                                        namePrefix="description"
                                        config={values.description}
                                        hasError={fieldHasError("description")}
                                        aiFill={values.description.aiFill}
                                    >
                                        <LocalizedStrict
                                            id="merchant-menu-templates-description-value-field"
                                            attrs={{ placeholder: true }}
                                        >
                                            <FormikTextField
                                                name="description.value"
                                                placeholder="Description text…"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                        </LocalizedStrict>
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.PRICE}
                                        namePrefix="price"
                                        config={values.price}
                                        hasError={fieldHasError("price")}
                                        aiFill={values.price.aiFill}
                                    >
                                        <Stack direction="row" alignItems="center" spacing=".5rem">
                                            <Box className={classes.priceField}>
                                                <LocalizedStrict
                                                    id="merchant-menu-templates-price-value-field"
                                                    attrs={{ label: true }}
                                                >
                                                    <FormikCurrencyField name="price.value" label="Price" />
                                                </LocalizedStrict>
                                            </Box>
                                            <Typography variant="body2" className={classes.currency}>
                                                DKK
                                            </Typography>
                                        </Stack>
                                        <Box className={classes.sameWeekToggle}>
                                            <LocalizedStrict id="merchant-menu-templates-price-same-week-checkbox">
                                                <FormikSwitch name="price.sameAllWeek" labelPlacement="end">
                                                    Same price all week
                                                </FormikSwitch>
                                            </LocalizedStrict>
                                        </Box>
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.ALLERGENS}
                                        namePrefix="allergens"
                                        config={values.allergens}
                                        hasError={fieldHasError("allergens")}
                                        aiFill={values.allergens.aiFill}
                                    >
                                        <FormikAllergensField name="allergens.value" />
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.MARKETING_TAG}
                                        namePrefix="marketingTag"
                                        config={values.marketingTag}
                                        hasError={fieldHasError("marketingTag")}
                                        aiFill={values.marketingTag.aiFill}
                                    >
                                        <LocalizedStrict
                                            id="merchant-menu-templates-marketing-tag-value-field"
                                            attrs={{ label: true, helperText: true }}
                                        >
                                            <FormikTextField
                                                name="marketingTag.value"
                                                label="Marketing tag"
                                                fullWidth
                                            />
                                        </LocalizedStrict>
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.IMAGE}
                                        namePrefix="image"
                                        config={values.image}
                                        hasError={fieldHasError("image")}
                                    >
                                        <LocalizedStrict
                                            id="merchant-menu-templates-image-upload"
                                            attrs={{ label: true }}
                                        >
                                            <FormikImageUpload
                                                label="Upload image"
                                                name="image.photo"
                                                typeOfEntity="dayMenu"
                                            />
                                        </LocalizedStrict>
                                    </DishFieldCard>
                                </Stack>
                            </Stack>

                            <Stack spacing=".5rem">
                                <Stack>
                                    <LocalizedStrict id="merchant-menu-templates-dish-details-title">
                                        <Typography variant="h6">Details</Typography>
                                    </LocalizedStrict>
                                    <LocalizedStrict id="merchant-menu-templates-dish-details-subtitle">
                                        <Typography variant="caption" className={classes.sectionSubtitle}>
                                            shown in a dialog in the week menu
                                        </Typography>
                                    </LocalizedStrict>
                                </Stack>

                                <Stack spacing="1rem">
                                    <DishFieldCard
                                        fieldKey={DishParameterKey.INGREDIENTS}
                                        namePrefix="ingredients"
                                        config={values.ingredients}
                                        hasError={fieldHasError("ingredients")}
                                    >
                                        <LocalizedStrict
                                            id="merchant-menu-templates-ingredients-value-field"
                                            attrs={{ placeholder: true }}
                                        >
                                            <FormikTextField
                                                name="ingredients.value"
                                                placeholder="e.g. Tomatoes, basil, garlic, olive oil…"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                        </LocalizedStrict>
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.NUTRITION}
                                        namePrefix="nutrition"
                                        config={values.nutrition}
                                        hasError={fieldHasError("nutrition")}
                                    >
                                        <LocalizedStrict
                                            id="merchant-menu-templates-nutrition-value-field"
                                            attrs={{ placeholder: true }}
                                        >
                                            <FormikTextField
                                                name="nutrition.value"
                                                placeholder="e.g. 320 kcal · 12g P · 40g C · 8g F"
                                                fullWidth
                                            />
                                        </LocalizedStrict>
                                    </DishFieldCard>

                                    <DishFieldCard
                                        fieldKey={DishParameterKey.CO2_FOOTPRINT}
                                        namePrefix="co2Footprint"
                                        config={values.co2Footprint}
                                        hasError={fieldHasError("co2Footprint")}
                                    >
                                        <Stack direction="row" alignItems="center" spacing=".5rem">
                                            <Box className={classes.priceField}>
                                                <FormikTextField name="co2Footprint.value" type="number" fullWidth />
                                            </Box>
                                            <LocalizedStrict id="merchant-menu-templates-co2-unit">
                                                <Typography variant="body2" className={classes.currency}>
                                                    kg CO₂ eq
                                                </Typography>
                                            </LocalizedStrict>
                                        </Stack>
                                    </DishFieldCard>
                                </Stack>
                            </Stack>
                        </Stack>
                    </StandardFormikErrorScrollWrapper>
                )
            }}
        </Formik>
    )
}
